import { useRef, useEffect, useMemo, useCallback, useState } from 'react';
import { Dropdown, ZipModal } from './components';
import { TIMEZONE } from '@root/lib/constants';
import { mergeState } from '@root/lib/utils/state';
import * as LogicService from '@root/lib/services/LogicService';
import * as MiddlewareService from '@root/lib/services/MiddlewareService';
import Calendar from '@root/lib/components/Icons/Calendar';
import Location from '@root/lib/components/Icons/Location';
import useBoolean from '@root/lib/hooks/useBoolean';
import dayjs from '@root/lib/packages/dayjs';
import useAsyncEffect from '@root/lib/hooks/useAsyncEffect';
import useAsyncCallback from '@root/lib/hooks/useAsyncCallback';

/**
 * Converts a string array into a string that is comparable via Object.equals
 *
 * @param {Array<string>} array
 */
const comparableStringArray = (array) => {
  return array.reduce((c, e) => `${c}|${e}`, '');
}

/**
 * Formats a date object into a human readable string
 *
 * @param {Date} date
 */
const formatDate = (date) => {
  if (!date) return '';
  const d = dayjs(date);

  const day = d.format('ddd');
  const month = d.format('MMM');
  return `${day}, ${month} ${d.date()}`;
}

/**
 * @typedef {Object} Region
 * @property {string} zipcode
 * @property {string} zone
 * @property {string} transit_time
 * @property {number} transit_days
 *
 * @param {{
 *   selectedDeliveryDate: Date|null;
 *   setRegion: (region: Region) => void;
 *   region: null|Region;
 * }} param
 */
const DeliveryOptionsHeader = ({
  selectedDeliveryDate,
  setRegion,
  region,
}) => {
  // const dropdownRef = useRef();
  // const [ time, setTime ] = useState({value: dayjs(), initialized: false, updating: false});
  const [ zipModalOpen, openZipModal, closeZipModal ] = useBoolean(region === null);
  const [ active, activate, deactivate ] = useBoolean(selectedDeliveryDate === null);
  const startDate = useMemo(() => {
    if (!selectedDeliveryDate) return null;
    return LogicService.calculateDateRange(selectedDeliveryDate);
  }, [selectedDeliveryDate]);

  /**
   * Closes the header
   */
  const close = useCallback(() => {
    closeZipModal();
    deactivate();
  }, []);

  /**
   * Closes the zip modal before passing the region to the setRegion function
   *
   * @type {setRegion}
   */
  const setRegionProxy = useAsyncCallback(async (isCancelled, region) => {
    await setRegion(region);
    !isCancelled() && close();
  }, [setRegion, close]);

  // Showing overlay when active
  useEffect(() => {
    active
      ? document.body.classList.add('delivery-header-select')
      : document.body.classList.remove('delivery-header-select');
  }, [active]);

  // // Getting the time from the server
  // useAsyncEffect(async (isCancelled) => {
  //   mergeState(setTime, {updating: true});

  //   try {
  //     const unix = await MiddlewareService.getTime();
  //     !isCancelled() && mergeState(setTime, {
  //       initialized: true,
  //       updating: false,
  //       value: dayjs.unix(unix).tz(TIMEZONE),
  //     });
  //   } finally {
  //     !isCancelled() && mergeState(setTime, {updating: false});
  //   }
  // }, []);

  return (
    <>
      <div className="delivery-header-container" style={{display: 'flex', alignItems: 'center'}}>
        {active ? (
          <>
            <div
              id="delivery-header_delivery-zipcode"
              className="delivery-header-zipcode form-field-inline"
              style={{margin: '0 10px 0 0', flexBasis: 'unset'}}
            >
              <label>Zip Code</label>
              <input id="delivery-input-zipcode"
                className="zipcode-validation"
                type="text"
                value={region?.zipcode ?? ''}
                onClick={openZipModal}
                readOnly
              />

            </div>
            <button
              id='save-btn'
              className="btn-secondary"
              disabled={!region?.zipcode} //|| !selectedDeliveryDate} // Disabling save button if deliver day or zip are not chosen
              style={{
                display: 'inline-block',
                margin: 0,
              }}
              onClick={close}
              children="Save"
            />
          </>
        ) : (
          <>
            <div className="delivery-selected-date-container">
              <div className="delivery-selected-date">  
                <Calendar /> <span>Delivery the week of {formatDate(startDate)}</span>
              </div>
            </div>
            <div className="delivery-selected-zipcode-container">
              <Location />
              <div className="delivery-selected-zipcode">{region.zipcode}</div>
            </div>
            <button
              className="btn-tertiary"
              onClick={activate}
              style={{
                display: 'inline-block',
                margin: 0,
              }}
            >
              Edit
            </button>
          </>
        )}
        <ZipModal
          setRegion={setRegionProxy}
          zip={region?.zipcode}
          open={zipModalOpen}
          onClose={closeZipModal}
        />
      </div>
    </>
  );
}

export default DeliveryOptionsHeader;
